import { Link } from "react-router-dom";
import {
  Facebook,
  Instagram,
  Linkedin,
  Youtube,
  MapPin,
  Phone,
  Mail,
  ArrowRight,
  ShieldCheck,
  Award,
  Hammer,
  MessageSquare,
} from "lucide-react";
import { Container } from "./Container.jsx";
import { BRAND, SOCIAL as SOCIAL_URLS } from "../../lib/brand.js";
import { usePageVisibility } from "../../hooks/usePageVisibility.js";

const COL_HOMES_BASE = [
  { to: "/home-designs", label: "Home Designs" },
  { to: "/house-land", label: "House & Land", visibilityKey: "houseLand" },
  { to: "/facades", label: "Facades" },
  { to: "/floor-plans", label: "Floor Plans" },
  { to: "/display-homes", label: "Display Homes", visibilityKey: "displayHomes" },
];

const COL_ABOUT = [
  { to: "/about/company-profile", label: "Company Profile" },
  { to: "/about/why-build-with-us", label: "Why Build with Us" },
  { to: "/about/where-we-build", label: "Where We Build" },
];

const COL_GET_STARTED = [
  { to: "/get-started/first-home-buyer", label: "First Home Buyer" },
  { to: "/get-started/investors", label: "Investors" },
  { to: "/get-started/our-process", label: "Our Process" },
  { to: "/contact-us/new-build", label: "New Build Enquiry" },
  { to: "/contact-us/preconstruction", label: "Pre-Construction" },
  { to: "/request-an-appointment", label: "Visit our display home" },
];

// Icon list paired with the URL constants from lib/brand.js. Setting a
// SOCIAL_URLS entry to "" hides that icon.
const SOCIAL = [
  { key: "facebook", icon: Facebook, label: "Facebook" },
  { key: "instagram", icon: Instagram, label: "Instagram" },
  { key: "linkedin", icon: Linkedin, label: "LinkedIn" },
  { key: "youtube", icon: Youtube, label: "YouTube" },
];

const TRUST_BADGES = [
  { icon: ShieldCheck, label: "Fixed-price guarantee" },
  { icon: Award, label: "Award-winning builds" },
  { icon: Hammer, label: "In-house trades" },
];

export function Footer() {
  const brand = BRAND;
  const year = new Date().getFullYear();
  const visibility = usePageVisibility();
  // Filter the Homes column to drop links the admin has hidden — entries
  // tagged with a `visibilityKey` are gated on the matching flag.
  const COL_HOMES = COL_HOMES_BASE.filter(
    (item) => !item.visibilityKey || visibility[item.visibilityKey]
  );

  return (
    <footer className="relative border-t border-border-subtle bg-bg-elevated">
      {/* Top accent line */}
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-accent/60 to-transparent" />

      {/* Trust strip */}
      <div className="border-b border-border-subtle/60 bg-bg-base/40">
        <Container className="grid grid-cols-1 gap-4 py-6 sm:grid-cols-3">
          {TRUST_BADGES.map(({ icon: Icon, label }) => (
            <div
              key={label}
              className="flex items-center justify-center gap-3 text-sm text-text-secondary sm:justify-start"
            >
              <span className="grid h-9 w-9 place-items-center rounded-full border border-accent/30 bg-accent/10 text-accent">
                <Icon className="h-4 w-4" />
              </span>
              <span>{label}</span>
            </div>
          ))}
        </Container>
      </div>

      <Container className="py-14 lg:py-20">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:gap-10">
          {/* Brand column — logo only */}
          <div className="lg:col-span-4">
            <Link to="/" className="inline-flex" aria-label={brand.name ?? "Shere-e-Punjab Homes"}>
              <img
                src="/logo.png"
                alt={brand.name ?? "Shere-e-Punjab Homes"}
                className="h-20 w-auto object-contain lg:h-24"
              />
            </Link>
            <p className="mt-6 text-sm leading-relaxed text-text-secondary">
              Bespoke custom homes, knockdown rebuilds, and house & land packages across Melbourne,
              Geelong, Ballarat and Bendigo. One team, one quote, one project lead — sketch to
              handover.
            </p>

            <div className="mt-6 space-y-3 text-sm">
              {brand.address && (
                <p className="flex items-start gap-2 text-text-secondary">
                  <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-accent" />
                  <span>
                    {brand.address}
                    {brand.serviceAreaLine && (
                      <span className="mt-1 block font-medium text-accent">
                        {brand.serviceAreaLine}
                      </span>
                    )}
                  </span>
                </p>
              )}
              {brand.phone && (
                <p className="flex items-center gap-2 text-text-secondary">
                  <Phone className="h-4 w-4 shrink-0 text-accent" />
                  <a href={`tel:${brand.phone.replace(/\s+/g, "")}`} className="hover:text-accent">
                    {brand.phone}
                  </a>
                </p>
              )}
              {brand.email && (
                <p className="flex items-center gap-2 text-text-secondary">
                  <Mail className="h-4 w-4 shrink-0 text-accent" />
                  <a href={`mailto:${brand.email}`} className="hover:text-accent">
                    {brand.email}
                  </a>
                </p>
              )}
            </div>

            <div className="mt-6 flex items-center gap-2">
              {SOCIAL.map(({ key, icon: Icon, label }) => {
                const href = SOCIAL_URLS[key];
                return href ? (
                  <a
                    key={key}
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={label}
                    className="flex h-9 w-9 items-center justify-center rounded-full border border-border-subtle text-text-secondary transition-colors hover:border-accent hover:bg-accent/10 hover:text-accent"
                  >
                    <Icon className="h-4 w-4" />
                  </a>
                ) : null;
              })}
            </div>
          </div>

          {/* Link columns */}
          <FooterColumn title="Homes" items={COL_HOMES} className="lg:col-span-2" />
          <FooterColumn title="About" items={COL_ABOUT} className="lg:col-span-2" />

          {/* Talk to our team — replaces the newsletter */}
          <div className="lg:col-span-4">
            <div className="rounded-2xl border border-accent/30 bg-gradient-to-br from-accent/15 via-bg-base/40 to-bg-elevated p-6 lg:p-7">
              <div className="flex items-start gap-3">
                <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-accent/20 text-accent">
                  <MessageSquare className="h-5 w-5" />
                </span>
                <div>
                  <p className="text-xs font-semibold uppercase tracking-widest text-accent">
                    Talk to our team
                  </p>
                  <h3 className="mt-1 font-display text-2xl text-text-primary">
                    Real builders, real answers
                  </h3>
                </div>
              </div>
              <p className="mt-4 text-sm text-text-secondary">
                {"Every enquiry lands with a real person on our team — no chatbots, no call centres. We reply within one business day, every business day."}
              </p>
              <div className="mt-6 grid gap-3 sm:grid-cols-2">
                {brand.phone && (
                  <a
                    href={`tel:${brand.phone.replace(/\s+/g, "")}`}
                    className="inline-flex items-center justify-center gap-2 rounded-md border border-border bg-bg-elevated-2 px-4 py-3 text-sm text-text-primary transition-colors hover:border-accent hover:text-accent"
                  >
                    <Phone className="h-4 w-4 text-accent" />
                    Call us
                  </a>
                )}
                <Link
                  to="/contact-us"
                  className="inline-flex items-center justify-center gap-2 rounded-md bg-accent px-4 py-3 text-sm font-medium text-bg-base transition-colors hover:bg-accent-hover"
                >
                  Send a message
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Get Started quick tiles */}
        <div className="mt-10 grid grid-cols-1 gap-3 border-t border-border-subtle pt-10 sm:grid-cols-2 lg:grid-cols-4">
          {COL_GET_STARTED.slice(0, 4).map((item) => (
            <Link
              key={item.to}
              to={item.to}
              className="group flex items-center justify-between rounded-lg border border-border-subtle bg-bg-base/40 px-4 py-3 text-sm text-text-secondary transition-colors hover:border-accent hover:text-accent"
            >
              <span>{item.label}</span>
              <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
            </Link>
          ))}
        </div>
      </Container>

      {/* Bottom strip */}
      <div className="border-t border-border-subtle bg-bg-base/40">
        <Container className="flex flex-col gap-4 py-6 lg:flex-row lg:items-center lg:justify-between">
          <p className="text-xs text-text-muted">
            © {year} {brand.name ?? "Shere-e-Punjab Homes"}. All rights reserved. ABN registered builder.
          </p>

          <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-xs">
            <Link to="/legal/privacy-policy" className="text-text-secondary hover:text-accent">
              Privacy Policy
            </Link>
            <Link to="/legal/terms" className="text-text-secondary hover:text-accent">
              Terms & Conditions
            </Link>
            <Link to="/legal/due-diligence" className="text-text-secondary hover:text-accent">
              Due Diligence
            </Link>
            <Link to="/contact-us" className="text-text-secondary hover:text-accent">
              Contact
            </Link>
          </div>
        </Container>
      </div>
    </footer>
  );
}

function FooterColumn({ title, items, className = "" }) {
  return (
    <nav className={className}>
      <p className="text-xs font-semibold uppercase tracking-widest text-accent">{title}</p>
      <ul className="mt-5 space-y-3 text-sm">
        {items.map((item) => (
          <li key={item.to}>
            <Link
              to={item.to}
              className="group inline-flex items-center gap-2 text-text-secondary transition-colors hover:text-accent"
            >
              <span className="h-px w-0 bg-accent transition-all duration-300 group-hover:w-3" />
              {item.label}
            </Link>
          </li>
        ))}
      </ul>
    </nav>
  );
}